import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  Stack,
} from "@mui/material";
import toast from "react-hot-toast";
import { useTrackerConfig, useSaveTrackerConfig } from "../api/trackerApi";
import { DEPARTMENTS } from "../constants/departments";

/**
 * Rename Tracker's stages per department, and set how many working days each
 * one is expected to take.
 *
 * The stage codes never change — only what a department calls them. That's
 * what lets one pipeline serve Tech, Marketing, SCM and HR without any of
 * them reading someone else's vocabulary.
 */
export default function TrackerStagesPanel() {
  const { data, isLoading } = useTrackerConfig();
  const save = useSaveTrackerConfig();
  const [draft, setDraft] = useState(null);
  const [dept, setDept] = useState(DEPARTMENTS[0]);

  useEffect(() => {
    if (data?.config) setDraft(JSON.parse(JSON.stringify(data.config)));
  }, [data]);

  const stageCodes = data?.stageCodes || [];
  const current = draft?.[dept];

  const dirty = useMemo(() => {
    if (!draft || !data?.config) return false;
    return JSON.stringify(draft) !== JSON.stringify(data.config);
  }, [draft, data]);

  if (isLoading || !draft) {
    return <Typography color="text.secondary">Loading Tracker stages…</Typography>;
  }

  const setLabel = (code, value) =>
    setDraft((d) => ({ ...d, [dept]: { ...d[dept], labels: { ...d[dept].labels, [code]: value } } }));

  const setDuration = (code, value) =>
    setDraft((d) => ({
      ...d,
      [dept]: {
        ...d[dept],
        durations: { ...d[dept].durations, [code]: value === "" ? null : Number(value) },
      },
    }));

  const submit = async () => {
    try {
      await save.mutateAsync(draft);
      toast.success("Tracker stages saved");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save");
    }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Tracker stages
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What each department calls the seven stages, and the standard ETA for each in working days.
        Leave the ETA blank for stages with no natural deadline — intake and the closing stage usually have none.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        Renaming a stage changes it for everyone in that department. The underlying flow stays the same,
        so nothing in-flight is disturbed.
      </Alert>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={dept} onChange={(_e, v) => setDept(v)} variant="scrollable" scrollButtons="auto">
          {DEPARTMENTS.map((d) => (
            <Tab key={d} value={d} label={d === "CSM" ? "SCM" : d} sx={{ textTransform: "none", fontWeight: 600 }} />
          ))}
        </Tabs>
      </Paper>

      {current ? (
        <Stack spacing={1.5}>
          {stageCodes.map((code) => (
            <Paper key={code} variant="outlined" sx={{ p: 1.5, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Typography
                sx={{ minWidth: 110, fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary" }}
              >
                {code}
              </Typography>
              <TextField
                size="small"
                label="Shown as"
                value={current.labels?.[code] ?? ""}
                onChange={(e) => setLabel(code, e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
              />
              <TextField
                size="small"
                type="number"
                label="ETA (working days)"
                value={current.durations?.[code] ?? ""}
                onChange={(e) => setDuration(code, e.target.value)}
                placeholder="none"
                sx={{ width: 140 }}
                inputProps={{ min: 1 }}
              />
            </Paper>
          ))}

          <Box sx={{ display: "flex", gap: 1, pt: 1 }}>
            <Button variant="contained" onClick={submit} disabled={!dirty || save.isPending}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setDraft(JSON.parse(JSON.stringify(data.config)))}
              disabled={!dirty}
            >
              Reset
            </Button>
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
}
